import { h, RefObject } from 'preact';
import UIElement from '../internal/UIElement/UIElement';
import UPIComponent from './components/UPIComponent';
import { Await } from '../internal/Await';
import { QRLoader } from '../internal/QRLoader';
import { UPIConfiguration, UpiMode, UpiPaymentData, UpiType } from './types';
import { TxVariants } from '../tx-variants';
import isMobile from '../../utils/isMobile';
import { UPI_MODE } from './constants';
import { ICore } from '../../types';

/**
 * For mobile:
 * We should show upi_intent depending on if `apps` are returned in /paymentMethods response.
 *
 * For non-mobile:
 * We should never show the upi_intent (ignore `apps` in /paymentMethods response)
 * Show upi_qr as default
 */

class UPI extends UIElement<UPIConfiguration> {
    public static readonly type = TxVariants.upi;
    public static readonly txVariants = [TxVariants.upi, TxVariants.upi_qr, TxVariants.upi_intent];
    private mode: UpiMode;
    constructor(checkout: ICore, props: UPIConfiguration) {
        super(checkout, props);
        // @ts-ignore: Accessing deprecated prop to provide migration warning
        const { defaultMode: deprecatedDefaultMode } = props;
        if (deprecatedDefaultMode) {
            console.warn('[Adyen Checkout] UPI configuration property "defaultMode" is deprecated and will be removed in a future version.');
        }
        this.mode = isMobile() ? UPI_MODE.INTENT : UPI_MODE.QR_CODE;
    }

    formatProps(props: UPIConfiguration): UPIConfiguration {
        const { apps = [] } = props;
        const hasIntentApps = apps.length > 0;
        if (isMobile() && hasIntentApps) {
            // Mobile with UPI apps
            return {
                ...super.formatProps(props),
                apps
            };
        }

        this.mode = UPI_MODE.QR_CODE;
        return {
            ...super.formatProps(props),
            apps: []
        };
    }

    public get isValid(): boolean {
        return this.state.isValid;
    }

    public formatData(): UpiPaymentData {
        const { app } = this.state.data || {};

        return {
            paymentMethod: {
                type: this.paymentType,
                ...(this.paymentType === TxVariants.upi_intent && app?.id && { appId: app.id })
            }
        };
    }

    get paymentType(): UpiType {
        if (this.mode === UPI_MODE.QR_CODE) {
            return TxVariants.upi_qr;
        }
        return TxVariants.upi_intent;
    }

    protected override componentToRender(): h.JSX.Element {
        const { type, url, paymentMethodType } = this.props;

        const isAutoPay = !!this.props.mandate;

        switch (type) {
            case 'qrCode':
                return (
                    <QRLoader
                        {...this.props}
                        qrCodeData={this.props.qrCodeData ? encodeURIComponent(this.props.qrCodeData) : null}
                        type={TxVariants.upi_qr}
                        brandLogo={this.props.brandLogo || this.icon}
                        onComplete={this.onComplete}
                        introduction={this.props.i18n.get('upi.qrCodeWaitingMessage')}
                        countdownTime={this.props.countdownTime ?? 5}
                        onActionHandled={this.onActionHandled}
                        showAmount={!isAutoPay}
                    />
                );
            case 'await':
                return (
                    <Await
                        url={url}
                        type={paymentMethodType}
                        showCountdownTimer
                        shouldRedirectAutomatically
                        showAmount={!isAutoPay}
                        countdownTime={this.props.countdownTime ?? 5}
                        clientKey={this.props.clientKey}
                        paymentData={this.props.paymentData}
                        onActionHandled={this.onActionHandled}
                        onError={this.props.onError}
                        messageText={this.props.i18n.get('upi.vpaWaitingMessage')}
                        awaitText={this.props.i18n.get('await.waitForConfirmation')}
                        onComplete={this.onComplete}
                        brandLogo={this.icon}
                    />
                );
            default:
                return (
                    <UPIComponent
                        ref={(ref: RefObject<typeof UPIComponent>) => {
                            this.componentRef = ref;
                        }}
                        payButton={this.payButton}
                        onChange={this.setState}
                        apps={this.props.apps}
                        mode={this.mode}
                        showPayButton={this.props.showPayButton}
                        mandate={this.props.mandate}
                    />
                );
        }
    }
}

export default UPI;
